import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Modal, Form, Row, Col, Alert, Tabs, Tab } from 'react-bootstrap';
import { FaUserPlus, FaFileCsv, FaFileDownload, FaTrashAlt, FaBan, FaCheck, FaEdit } from 'react-icons/fa';
import { personnelAPI } from '../../utils/api';
import { jwtDecode } from 'jwt-decode';
import { currentUserHasPermission } from '../../utils/permissionUtils';
import { API_BASE_URL } from '../../config';
import notificationService from '../../services/notificationService';


const PersonnelManagement = () => {
  const [personnel, setPersonnel] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: ''
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: ''
  });
  const [bulkFile, setBulkFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('');
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);


  // Get and decode the token
  let permissions = [];

  try {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user'));

    if (token) {
      const decoded = jwtDecode(token);
      const rawPermissions = decoded?.permissions;

      if (typeof rawPermissions === 'string') {
        permissions = JSON.parse(rawPermissions);
      } else if (Array.isArray(rawPermissions)) {
        permissions = rawPermissions;
      }
    } else if (user?.permissions) {
      permissions = Array.isArray(user.permissions)
        ? user.permissions
        : JSON.parse(user.permissions);
    }
  } catch (err) {
    permissions = [];
  }

  const hasPermission = (perm) => permissions.includes(perm);



  useEffect(() => {
    // Fetch personnel from the API
    const fetchPersonnel = async () => {
      try {
        const response = await personnelAPI.getAll();
        setPersonnel(response.data);
        setError('');
      } catch (error) {
        setError("Failed to load personnel data. Please try again.");
      }
    };

    fetchPersonnel();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  const openEditModal = (person) => {
    setSelectedPersonnel(person);
    setEditFormData({
      name: person.name,
      email: person.email,
      phone: person.phone,
      position: person.position
    });
    setError(''); // Clear any previous errors
    setShowEditModal(true);
  };

  const handleEditPersonnel = async (e) => {
    e.preventDefault();

    if (!editFormData.name || !editFormData.email || !editFormData.phone || !editFormData.position) {
      setError("Please fill all required fields");
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Use the API utility instead of fetch directly
      const response = await personnelAPI.update(selectedPersonnel.id, editFormData);
      
      // Update the personnel directly in the state instead of fetching again
      setPersonnel(prevPersonnel => 
        prevPersonnel.map(person => 
          person.id === selectedPersonnel.id 
            ? { 
                ...person, 
                name: editFormData.name,
                email: editFormData.email,
                phone: editFormData.phone,
                position: editFormData.position
              }
            : person
        )
      );

      // Trigger notification
      notificationService.triggerPersonnelAction('personnel-updated', editFormData.name);

      setShowEditModal(false);
      setSuccess('Personnel updated successfully!');
      setEditFormData({
        name: '',
        email: '',
        phone: '',
        position: ''
      });

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError("Failed to update personnel. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPersonnel = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.position) {
      setError("Please fill all required fields");
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await personnelAPI.create(formData);

      const newPersonnel = {
        id: response.data.personnel.id,
        name: response.data.personnel.name,
        email: response.data.personnel.email,
        phone: response.data.personnel.phone,
        position: response.data.personnel.position,
        status: response.data.personnel.status,
        createdAt: new Date().toISOString()
      };

      // Update state by adding the new personnel to the beginning of the list
      setPersonnel(prevPersonnel => [newPersonnel, ...prevPersonnel]);

      notificationService.triggerPersonnelAction('personnel-added', newPersonnel.name);

      setShowAddModal(false);
      setSuccess('Personnel added successfully! Credentials have been generated.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        position: ''
      });

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to add personnel. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  const handleBulkUpload = async (e) => {
    e.preventDefault();
    
    if (!bulkFile) {
      setError('Please select a file to upload');
      return;
    }
    
    try {
      // Create form data for file upload
      const formData = new FormData();
      formData.append('file', bulkFile);
      
      // Use the API utility instead of fetch directly
      const response = await personnelAPI.bulkUpload(formData);
      
      // For bulk upload, we need to fetch the updated list since we don't know exactly what was added
      const personnelResponse = await personnelAPI.getAll();
      setPersonnel(personnelResponse.data);
      
      setShowBulkModal(false);
      setBulkFile(null);
      setSuccess(`Personnel bulk upload successful! ${response.data.count || 'Multiple'} new personnel added.`);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError("Failed to upload personnel. Please try again.");
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleToggleStatus = async () => {
    try {
      // Use the API utility instead of fetch directly
      const response = await personnelAPI.toggleStatus(selectedPersonnel.id);
      
      const wasActive = selectedPersonnel.status === 'active';
      
      // Update the personnel status directly in the state
      setPersonnel(prevPersonnel => 
        prevPersonnel.map(person => 
          person.id === selectedPersonnel.id 
            ? { ...person, status: person.status === 'active' ? 'inactive' : 'active' }
            : person
        )
      );
      
      // Trigger notification
      notificationService.triggerPersonnelAction('personnel-disabled', selectedPersonnel.name);
      
      setShowConfirmModal(false);
      setSuccess(`Personnel status ${wasActive ? 'disabled' : 'enabled'} successfully!`);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError("Failed to update personnel status. Please try again.");
      setShowConfirmModal(false);
      setTimeout(() => setError(''), 3000);
    }
  };

  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentialsOptions, setCredentialsOptions] = useState({
    resetPassword: false,
    includePassword: true
  });
  const [selectedPersonnelId, setSelectedPersonnelId] = useState(null);

  const handleCredentialsOptionChange = (e) => {
    const { name, checked } = e.target;
    setCredentialsOptions({
      ...credentialsOptions,
      [name]: checked
    });
  };

  const openCredentialsModal = (personnelId) => {
    setSelectedPersonnelId(personnelId);
    setShowCredentialsModal(true);
  };

  const handleDownloadCredentials = async () => {
    try {
      if (!selectedPersonnelId) {
        throw new Error('No personnel selected');
      }

      // Use the API utility instead of fetch directly
      const response = await personnelAPI.downloadCredentials(
        selectedPersonnelId, 
        credentialsOptions.resetPassword,
        credentialsOptions.includePassword
      );
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(response.data);
      
      // Create a temporary link element
      const a = document.createElement('a');
      a.href = url;
      a.download = `credentials-${selectedPersonnelId}.pdf`;
      
      // Append to the document and click to trigger download
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setShowCredentialsModal(false);
      setDownloadSuccess(true);
      setSuccess('Credentials downloaded successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
        setDownloadSuccess(false);
      }, 3000);
    } catch (error) {
      setError("Failed to download credentials. Please try again.");
      setTimeout(() => setError(''), 3000);
    }
  };

  const confirmToggleStatus = (person) => {
    setSelectedPersonnel(person);
    setShowConfirmModal(true);
  };

  const confirmDeletePersonnel = (person) => {
    setSelectedPersonnel(person);
    setShowDeleteModal(true);
  };

  const handleDeletePersonnel = async () => {
    try {
      const personnelName = selectedPersonnel.name;
      
      await personnelAPI.delete(selectedPersonnel.id);
      
      // Remove the personnel from the state
      setPersonnel(prevPersonnel => 
        prevPersonnel.filter(person => person.id !== selectedPersonnel.id)
      );
      
      // Trigger notification
      notificationService.triggerPersonnelAction('personnel-deleted', personnelName);
      
      setShowDeleteModal(false);
      setSuccess('Personnel deleted successfully!');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError("Failed to delete personnel. Please try again.");
      setShowDeleteModal(false);
      setTimeout(() => setError(''), 3000);
    }
  };

  const downloadSampleFile = async () => {
    try {
      // Use the API utility to download the template
      const response = await personnelAPI.downloadTemplate();
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(response.data);
      
      // Create a temporary link element
      const a = document.createElement('a');
      a.href = url;
      a.download = 'personnel_template.csv';
      
      // Append to the document and click to trigger download
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 3000);
    } catch (error) {
      setError("Failed to download template. Please try again.");
      setTimeout(() => setError(''), 3000);
    }
  };



  const filteredPersonnel = personnel.filter(person => 
    person.name.toLowerCase().includes(filter.toLowerCase()) ||
    person.email.toLowerCase().includes(filter.toLowerCase()) ||
    person.position.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Personnel Management</h1>
        <div>
          <Button 
            variant="outline-primary" 
            className="me-2"
            onClick={() => setShowBulkModal(true)}
          >
            <FaFileCsv className="me-2" />
            Bulk Upload
          </Button>
          <Button 
            variant="primary"
                            onClick={() => {
                  setError(''); // Clear any previous errors
                  setShowAddModal(true);
                }}
          >
            <FaUserPlus className="me-2" />
            Add Personnel
          </Button>
        </div>
      </div>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <Card className="mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Search by name, email, or position..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          
          <div className="table-responsive">
            <Table striped hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Position</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPersonnel.map((person, index) => (
                  <tr key={person.id}>
                    <td title={`DB id: ${person.id}`}>{index + 1}</td>
                    <td>{person.name}</td>
                    <td>{person.email}</td>
                    <td>{person.phone}</td>
                    <td>{person.position}</td>
                    <td>
                      <span className={`badge ${person.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                        {person.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <Button 
                        variant="outline-primary"
                        size="sm"
                        onClick={() => openCredentialsModal(person.id)}
                        className="me-2"
                        title="Download Credentials"
                      >
                        <FaFileDownload />
                      </Button>
                      
                      <Button 
                        variant="outline-warning"
                        size="sm"
                        onClick={() => openEditModal(person)}
                        className="me-2"
                        title={
                          currentUserHasPermission('personnel', 'edit')
                            ? 'Edit Personnel Details'
                            : 'You do not have permission to edit personnel'
                        }
                        disabled={!currentUserHasPermission('personnel', 'edit')}
                      >
                        <FaEdit />
                      </Button>
                      
                      <Button 
                        variant={person.status === 'active' ? 'outline-danger' : 'outline-success'} 
                        size="sm"
                        onClick={() => confirmToggleStatus(person)}
                        className="me-2"
                        title={
                          currentUserHasPermission('personnel', 'disable')
                            ? person.status === 'active' ? 'Disable Account' : 'Enable Account'
                            : 'You do not have permission to toggle personnel status'
                        }
                        disabled={!currentUserHasPermission('personnel', 'disable')}
                      >
                        {person.status === 'active' ? <FaBan /> : <FaCheck />}
                      </Button>

                      <Button 
                        variant="outline-danger"
                        size="sm"
                        onClick={() => confirmDeletePersonnel(person)}
                        title={
                          currentUserHasPermission('personnel', 'delete')
                            ? 'Delete Personnel'
                            : 'You do not have permission to delete personnel'
                        }
                        disabled={!currentUserHasPermission('personnel', 'delete')}
                      >
                        <FaTrashAlt />
                      </Button>

                    </td>
                  </tr>
                ))}
                {filteredPersonnel.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center">No personnel found matching your search.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
      
      {/* Add Personnel Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Personnel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
          <Form onSubmit={handleAddPersonnel}>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone *</Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Position *</Form.Label>
              <Form.Control
                type="text"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={!currentUserHasPermission('personnel', 'create') || isLoading}
                title={
                  !currentUserHasPermission('personnel', 'create')
                    ? 'You do not have permission to add personnel'
                    : ''
                }
              >
                {isLoading ? 'Adding...' : 'Add Personnel'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* Bulk Upload Modal */}
      <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Bulk Upload Personnel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <div className="d-flex align-items-center">
              <div>
                First time? Download our sample template to see the required format.
              </div>
              <Button variant="outline-primary" size="sm" className="ms-2" onClick={downloadSampleFile}>
                <FaFileDownload className="me-1" /> Template
              </Button>
            </div>
          </Alert>
          
          {downloadSuccess && (
            <Alert variant="success">
              Sample file downloaded successfully!
            </Alert>
          )}
          
          <Form onSubmit={handleBulkUpload}>
            <Form.Group className="mb-3">
              <Form.Label>Upload CSV or Excel File</Form.Label>
              <Form.Control
                type="file"
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={(e) => setBulkFile(e.target.files[0])}
                required
              />
              <Form.Text className="text-muted">
                Supported formats: CSV, XLSX
              </Form.Text>
            </Form.Group>
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowBulkModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={!bulkFile}>
                Upload and Process
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* Confirm Toggle Status Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedPersonnel?.status === 'active' ? 'Deactivate' : 'Activate'} Personnel
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPersonnel?.status === 'active' ? (
            <p>
              Are you sure you want to deactivate {selectedPersonnel?.name}? 
              They will no longer be able to log in to the system.
            </p>
          ) : (
            <p>
              Are you sure you want to activate {selectedPersonnel?.name}? 
              They will be able to log in to the system again.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button 
            variant={selectedPersonnel?.status === 'active' ? 'danger' : 'success'} 
            onClick={() => handleToggleStatus(selectedPersonnel?.id)}
          >
            {selectedPersonnel?.status === 'active' ? 'Deactivate' : 'Activate'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Credentials Download Modal */}
      <Modal show={showCredentialsModal} onHide={() => setShowCredentialsModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Download Personnel Credentials</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Select options for the credentials PDF:</p>
          <Form>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="reset-password"
                name="resetPassword"
                label="Reset password (generates a new random password)"
                checked={credentialsOptions.resetPassword}
                onChange={handleCredentialsOptionChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="include-password"
                name="includePassword"
                label="Include password in PDF"
                checked={credentialsOptions.includePassword}
                onChange={handleCredentialsOptionChange}
              />
              <Form.Text className="text-muted">
                If not resetting the password, this will generate a temporary password for the PDF only.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCredentialsModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleDownloadCredentials}>
            Download Credentials
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Personnel Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Personnel Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
          <Form onSubmit={handleEditPersonnel}>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={editFormData.name}
                onChange={handleEditInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={editFormData.email}
                onChange={handleEditInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone *</Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                value={editFormData.phone}
                onChange={handleEditInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Position *</Form.Label>
              <Form.Control
                type="text"
                name="position"
                value={editFormData.position}
                onChange={handleEditInputChange}
                required
              />
            </Form.Group>
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={!currentUserHasPermission('personnel', 'edit') || isLoading}
                title={
                  !currentUserHasPermission('personnel', 'edit')
                    ? 'You do not have permission to edit personnel'
                    : ''
                }
              >
                {isLoading ? 'Updating...' : 'Update Personnel'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete Personnel Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Personnel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete <strong>{selectedPersonnel?.name}</strong>? 
            This action cannot be undone and will permanently remove the personnel from the system.
          </p>
          <Alert variant="warning">
            <strong>Warning:</strong> This will also delete all associated data including check-ins, reports, and other records.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeletePersonnel}
            disabled={!currentUserHasPermission('personnel', 'delete')}
          >
            Delete Personnel
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PersonnelManagement;
